import React, { memo, useCallback } from "react";

import { Box, List, ListItem } from "@origin-digital/ods-core";

import { threePhasesExamplesBase64 } from "@/assets/threePhasesExamples";
import {
  RadioGroupInput,
  RouterActions,
  StyledAccordion,
  StyledHeading,
  StyledText,
} from "@/components";
import { StyledImage } from "@/components/StyledImage";
import { StyledQuestion } from "@/components/StyledQuestion";
import { useFieldValidation } from "@/hooks";

import { threePhase } from "./content.json";
import {
  isBatteryJourney,
  isSolarBatteryJourney,
} from "@/features/SolarForm/utils/formJourney";
import { useFormContext } from "react-hook-form";

export const ThreePhase = memo(
  ({
    backRoute,
    nextRoute,
    batteryNextRoute,
    solarBatteryNextRoute,
  }: {
    backRoute: () => Promise<string>;
    nextRoute: () => Promise<string>;
    batteryNextRoute: () => Promise<string>;
    solarBatteryNextRoute: () => Promise<string>;
  }) => {
    const { isValid } = useFieldValidation({ fields: ["threePhase"] });

    const { getValues } = useFormContext();
    const systemType = getValues("systemType");

    const handleNextRoute = useCallback(() => {
      if (isSolarBatteryJourney(systemType)) {
        return solarBatteryNextRoute();
      } else if (isBatteryJourney(systemType)) {
        return batteryNextRoute();
      }
      return nextRoute();
    }, [systemType]);

    return (
      <StyledQuestion>
        <div>
          <StyledHeading>{threePhase.heading}</StyledHeading>
          <RadioGroupInput
            data-id={threePhase.id}
            name="threePhase"
            label=""
            options={threePhase.option}
          />
          <StyledAccordion
            id={`${threePhase.id}-accordion`}
            summary={threePhase.description}
          >
            <StyledText>{threePhase.descriptionExpand.label}</StyledText>
            <Box>
              <StyledText>
                {threePhase.descriptionExpand.checkingSteps.label}
              </StyledText>
              <List type="decimal">
                {threePhase.descriptionExpand.checkingSteps.content.map(
                  (step) => (
                    <ListItem key={Math.random()}>{step}</ListItem>
                  )
                )}
              </List>
            </Box>
            <Box marginTop="large">
              <StyledHeading variant="h4" tone="neutralDark">
                {threePhase.descriptionExpand.examples.label}
              </StyledHeading>
              {/* TODO: Temporary using Base64 Image until setting up hosting the image in our own microsite  */}
              <StyledImage
                dataId="three-phases-example"
                src={`data:image/png;base64,${threePhasesExamplesBase64}`}
                alt={threePhase.descriptionExpand.examples.label}
              />
              <StyledText>
                {threePhase.descriptionExpand.examples.description}
              </StyledText>
            </Box>
          </StyledAccordion>
        </div>
        <RouterActions
          onBackRoute={backRoute}
          onNextRoute={handleNextRoute}
          onNext={isValid}
          nextLabel={threePhase.nextLabel}
        />
      </StyledQuestion>
    );
  }
);
